import { useQuery } from '@apollo/client';
import { faExternalLink, faPlus } from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS } from '../../queries.ts';
import {
    CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_ADD_PATH,
    CUSTOMER_INTEGRATION_ERP_PATH,
} from '../../constants.ts';
import { Link } from 'react-router-dom';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { Outlet } from 'react-router';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import Table from '../../../core/components/Table.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { DynamicsFinanceOperationsRow } from './DynamicsFinanceOperationsRow.tsx';

const AddIcon = withIcon(faPlus);
const ExternalLinkIcon = withIcon(faExternalLink);

const DynamicsFinanceOperationsIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const {
        data: data,
        loading: loading,
        refetch: refetch,
    } = useQuery(GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.dynamicsFinanceOperationsIntegrations || [];

    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')}`;

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('dynamicsFinanceOperations.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <a href={t('dynamicsFinanceOperations.documentationLink')} target={'_blank'}>
                                <span className="flex gap-2 items-center">
                                    {t('dynamicsFinanceOperations.documentation')}
                                    <ExternalLinkIcon />{' '}
                                </span>
                            </a>
                        </Page.HeaderButton>

                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_ADD_PATH)}>
                                <AddIcon />
                                {t('dynamicsFinanceOperations.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-96 w-96">
                                    {t('dynamicsFinanceOperations.table.name')}
                                </Table.HeadCell>
                                <Table.HeadCell>{t('dynamicsFinanceOperations.table.orderChannels')}</Table.HeadCell>
                                <Table.HeadCell>{t('dynamicsFinanceOperations.table.rfqChannels')}</Table.HeadCell>
                                <Table.HeadCell>
                                    {t('dynamicsFinanceOperations.table.masterDataUpdatedAt')}
                                </Table.HeadCell>
                                <Table.HeadCell>{t('dynamicsFinanceOperations.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <DynamicsFinanceOperationsRow
                                        key={integration.id}
                                        integration={integration}
                                        refetch={refetch}
                                    />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={5} className="text-center text-content-medium">
                                        {t('dynamicsFinanceOperations.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch }} />
        </Layout>
    );
};

export default DynamicsFinanceOperationsIntegrationPage;
